import { Component, OnInit, OnDestroy, ViewChild, NgZone } from "@angular/core";
import { Subscription } from "rxjs";
import {
  ExportService,
  ExportOption,
  ExportType,
  AnalyticsService,
} from "../../../shared/index";
import {
  ChartDefinitions,
  ChartDefinition,
  AnalyticsType,
} from "../../chartDefinitions.config";
import { ActivatedRoute } from "@angular/router";

@Component({
  selector: "chart-details-page",
  templateUrl: "./chart-details-page.component.html",
  styleUrl: "./chart-details-page.component.css",
  standalone: false,
})
export class ChartDetailsPage implements OnInit, OnDestroy {
  sections = ChartDefinitions;
  analyticsTypeEnum = AnalyticsType;
  sectionsArray: { key: string; section: ChartDefinition; type: string[] }[] =
    [];
  filteredAndSortedSections: {
    type: AnalyticsType;
    charts: { key: string; section: ChartDefinition; type: string[] }[];
  }[] = [];
  currentTitle = "";
  currentSectionKey: string;
  formattedExportOptions: {
    chart: string;
    types: { label: string; value: ExportType }[];
  }[] = [];
  exporting: boolean = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private _exportService: ExportService,
    private _analyticsService: AnalyticsService,
    private _ngZone: NgZone,
    private _route: ActivatedRoute
  ) {
    //actions on before init, ex: load subscriptions

    this.subscriptions.push(
      this._exportService.exportOptionsUpdateEvent.subscribe((options) => {
        this.buildExportOptions(options);
      })
    );

    this.subscriptions.push(
      this._exportService.exportCompleteEvent.subscribe((result: boolean) => {
        this._ngZone.run(() => {
          this.exporting = false;
        });
      })
    );

    this._route.paramMap.subscribe((params) => {
      const chartKey = params.get("chartKey");
      if (chartKey && ChartDefinitions[chartKey]) {
        this.currentSectionKey = chartKey;
        this.currentTitle = ChartDefinitions[chartKey].title;
      } else {
        // Handle invalid chart key
        this.currentSectionKey = null;
        this.currentTitle = "";
      }
    });
  }

  get currentComponent() {
    return this.currentSectionKey
      ? this.sections[this.currentSectionKey].detailsComponent
      : null;
  }

  get currentChartTitle() {
    return this.currentSectionKey
      ? this.sections[this.currentSectionKey].title
      : "";
  }

  onSelect(event: Event, sectionKey: string): void {
    event.preventDefault();
    this.currentSectionKey = sectionKey;
    this.currentTitle = this.sections[sectionKey].title;
  }

  clearChart(): void {
    this.currentSectionKey = null;
    this.currentTitle = "";
  }

  onExportClick(chart: string, type: ExportType) {
    this._ngZone.run(() => {
      this.exporting = true;
    });
    setTimeout(() => {
      //delay click handling to allow loading spinner to show
      this._exportService.exportButtonClick(chart, type);
    }, 500);
  }

  refreshChart(): void {
    this._analyticsService.refreshDataPoints();
  }

  private loadSections(type: AnalyticsType): void {
    this.filteredAndSortedSections.push({
      type,
      charts: [
        ...this.sectionsArray
          .filter((entry) => entry.section.type === type) // Filter by type
          .sort((a, b) => {
            // Sort by sortOrder, then alphabetically by title
            if (a.section.sortOrder === b.section.sortOrder) {
              return a.section.title.localeCompare(b.section.title);
            }
            return a.section.sortOrder - b.section.sortOrder;
          }),
      ],
    });
  }

  /**
   * Build format the export options for the menu
   * @param options
   */
  private buildExportOptions(options: ExportOption[]) {
    if (!options || !options.length) {
      this.formattedExportOptions = [];
    }

    this.formattedExportOptions = options.map((option) => ({
      chart: option.chart,
      types: option.types.map((type) => ({
        label: type.toString(),
        value: type,
      })),
    }));
  }

  ngOnInit(): void {
    this.sectionsArray = Object.keys(this.sections).map((key) => ({
      key,
      section: this.sections[key],
      type: this.sections[key].chartTypeOptions.map((o) => o.toString()),
    }));
    this.loadSections(AnalyticsType.App);
    this.loadSections(AnalyticsType.User);
    this.loadSections(AnalyticsType.Manuals);
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }
  showFullDescription = false;

  onInfoClick(): void {
    this.showFullDescription = !this.showFullDescription;
  }

  closeFullDescription(): void {
    this.showFullDescription = false;
  }
}
